import React, { useState, useEffect } from 'react';
import { resetPassword, resetMfa, unlockUser, checkProtectedUser } from '../services/api';

function UserCard({ user, onUserUpdate }) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [passwordResult, setPasswordResult] = useState(null);
  const [mfaResult, setMfaResult] = useState(null);
  const [unlockResult, setUnlockResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forceChange, setForceChange] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isProtected, setIsProtected] = useState(false);
  const [protectedRoles, setProtectedRoles] = useState([]);

  useEffect(() => {
    checkProtectedUser(user.id)
      .then(result => {
        setIsProtected(result.isProtected);
        setProtectedRoles(result.roles || []);
      })
      .catch(() => setIsProtected(false));
  }, [user.id]);

  const markAsProtected = (err) => {
    if (err.isProtected || err.status === 403) {
      setIsProtected(true);
      setProtectedRoles([{ displayName: err.reason || 'Azure AD directory role (PIM)', type: 'pimEligible' }]);
      return true;
    }
    return false;
  };

  const handlePasswordReset = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await resetPassword(user.id, forceChange);
      setPasswordResult(result);
    } catch (err) {
      if (markAsProtected(err)) {
        setShowPasswordModal(false);
        return;
      }
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaReset = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await resetMfa(user.id);
      setMfaResult(result);
    } catch (err) {
      if (markAsProtected(err)) {
        setShowMfaModal(false);
        return;
      }
      setError('Failed to reset MFA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await unlockUser(user.id);
      setUnlockResult(result);
      if (onUserUpdate) {
        onUserUpdate({ ...user, accountEnabled: true });
      }
    } catch (err) {
      if (markAsProtected(err)) {
        setShowUnlockModal(false);
        return;
      }
      setError('Failed to unlock user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordResult(null);
    setError('');
    setForceChange(true);
  };

  const closeMfaModal = () => {
    setShowMfaModal(false);
    setMfaResult(null);
    setError('');
  };

  const closeUnlockModal = () => {
    setShowUnlockModal(false);
    setUnlockResult(null);
    setError('');
  };

  return (
    <>
      <div className="user-card">
        <div className="user-card-header">
          <div className="user-details">
            <h3>{user.displayName}</h3>
            <p className="user-email">{user.mail || user.userPrincipalName}</p>
          </div>
          <span className={`user-status ${user.accountEnabled ? 'active' : 'disabled'}`}>
            {user.accountEnabled ? 'Active' : 'Disabled'}
          </span>
        </div>

        <div className="user-card-body">
          {user.jobTitle && (
            <div className="user-info-item">
              <label>Job Title</label>
              <span>{user.jobTitle}</span>
            </div>
          )}
          {user.department && (
            <div className="user-info-item">
              <label>Department</label>
              <span>{user.department}</span>
            </div>
          )}
          <div className="user-info-item">
            <label>User ID</label>
            <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{user.id}</span>
          </div>
        </div>

        <div className="user-card-actions">
          <button
            className="action-btn primary"
            onClick={() => setShowPasswordModal(true)}
            disabled={isProtected}
            title={isProtected ? `Protected: ${protectedRoles.map(r => r.displayName).join(', ') || 'Admin role assigned'}` : ''}
          >
            Reset Password
          </button>
          <button
            className="action-btn danger"
            onClick={() => setShowMfaModal(true)}
            disabled={isProtected}
            title={isProtected ? `Protected: ${protectedRoles.map(r => r.displayName).join(', ') || 'Admin role assigned'}` : ''}
          >
            Reset MFA
          </button>
          {!user.accountEnabled && (
            <button
              className="action-btn warning"
              onClick={() => setShowUnlockModal(true)}
              disabled={isProtected}
              title={isProtected ? `Protected: ${protectedRoles.map(r => r.displayName).join(', ') || 'Admin role assigned'}` : ''}
            >
              Unlock User
            </button>
          )}
          {isProtected && (
            <div className="protected-notice">
              <p>Protected - RBAC Directory Role(s):</p>
              <ul style={{ margin: '4px 0 0 16px', fontSize: '12px' }}>
                {protectedRoles.length > 0 ? protectedRoles.map((role, i) => (
                  <li key={i}>{role.displayName}{role.type === 'pimEligible' ? ' (PIM Eligible)' : ''}</li>
                )) : <li>Admin role assigned</li>}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
            </div>
            <div className="modal-body">
              {!passwordResult ? (
                <>
                  <p>You are about to reset the password for:</p>
                  <p style={{ fontWeight: 600, margin: '12px 0' }}>{user.displayName}</p>
                  <p style={{ color: '#605e5c', fontSize: '14px' }}>{user.mail || user.userPrincipalName}</p>

                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="forceChange"
                      checked={forceChange}
                      onChange={(e) => setForceChange(e.target.checked)}
                    />
                    <label htmlFor="forceChange">
                      Require password change on next sign-in
                    </label>
                  </div>

                  {error && (
                    <div className="error-message" style={{ marginTop: '16px' }}>
                      <p>{error}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="success-message">
                    <p>Password has been reset successfully!</p>
                  </div>
                  <p style={{ marginTop: '16px' }}>Temporary Password:</p>
                  <div className="password-display">
                    {passwordResult.temporaryPassword}
                  </div>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(passwordResult.temporaryPassword)}
                  >
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                  <div className="warning-message" style={{ marginTop: '16px' }}>
                    <p>Please securely share this password with the user. They will {forceChange ? '' : 'not '}be required to change it on next sign-in.</p>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!passwordResult ? (
                <>
                  <button className="modal-btn cancel" onClick={closePasswordModal}>
                    Cancel
                  </button>
                  <button
                    className="modal-btn confirm"
                    onClick={handlePasswordReset}
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </>
              ) : (
                <button className="modal-btn confirm" onClick={closePasswordModal}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MFA Reset Modal */}
      {showMfaModal && (
        <div className="modal-overlay" onClick={closeMfaModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset MFA Methods</h3>
            </div>
            <div className="modal-body">
              {!mfaResult ? (
                <>
                  <div className="warning-message">
                    <p><strong>Warning:</strong> This will remove all MFA authentication methods for this user. They will need to re-register their MFA methods on next sign-in.</p>
                  </div>
                  <p style={{ marginTop: '16px' }}>You are about to reset MFA for:</p>
                  <p style={{ fontWeight: 600, margin: '12px 0' }}>{user.displayName}</p>
                  <p style={{ color: '#605e5c', fontSize: '14px' }}>{user.mail || user.userPrincipalName}</p>

                  {error && (
                    <div className="error-message" style={{ marginTop: '16px' }}>
                      <p>{error}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="success-message">
                    <p>All MFA methods have been removed successfully!</p>
                  </div>
                  {mfaResult.removedMethods && mfaResult.removedMethods.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <p style={{ fontWeight: 500 }}>Removed methods:</p>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        {mfaResult.removedMethods.map((method, index) => (
                          <li key={index} style={{ fontSize: '14px', color: '#605e5c' }}>
                            {method.type.replace('#microsoft.graph.', '').replace('AuthenticationMethod', '')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p style={{ marginTop: '16px', fontSize: '14px', color: '#605e5c' }}>
                    The user will be prompted to set up new MFA methods on their next sign-in.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!mfaResult ? (
                <>
                  <button className="modal-btn cancel" onClick={closeMfaModal}>
                    Cancel
                  </button>
                  <button
                    className="modal-btn danger"
                    onClick={handleMfaReset}
                    disabled={loading}
                  >
                    {loading ? 'Removing...' : 'Remove All MFA Methods'}
                  </button>
                </>
              ) : (
                <button className="modal-btn confirm" onClick={closeMfaModal}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unlock User Modal */}
      {showUnlockModal && (
        <div className="modal-overlay" onClick={closeUnlockModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Unlock User Account</h3>
            </div>
            <div className="modal-body">
              {!unlockResult ? (
                <>
                  <p>You are about to unlock the account for:</p>
                  <p style={{ fontWeight: 600, margin: '12px 0' }}>{user.displayName}</p>
                  <p style={{ color: '#605e5c', fontSize: '14px' }}>{user.mail || user.userPrincipalName}</p>

                  <div className="warning-message" style={{ marginTop: '16px' }}>
                    <p>This will enable the user account and allow the user to sign in again.</p>
                  </div>

                  {error && (
                    <div className="error-message" style={{ marginTop: '16px' }}>
                      <p>{error}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="success-message">
                    <p>User account has been unlocked successfully!</p>
                  </div>
                  <p style={{ marginTop: '16px', fontSize: '14px', color: '#605e5c' }}>
                    The user can now sign in to their account.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!unlockResult ? (
                <>
                  <button className="modal-btn cancel" onClick={closeUnlockModal}>
                    Cancel
                  </button>
                  <button
                    className="modal-btn confirm"
                    onClick={handleUnlock}
                    disabled={loading}
                  >
                    {loading ? 'Unlocking...' : 'Unlock Account'}
                  </button>
                </>
              ) : (
                <button className="modal-btn confirm" onClick={closeUnlockModal}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserCard;
